<template>
  <div
    :class="[
      'segmented-control',
      { 'is-primary': variant === 'primary', 'is-fit': fit },
    ]"
    role="tablist"
  >
    <button
      v-for="option in options"
      :key="String(option.value)"
      type="button"
      role="tab"
      :class="{ 'is-active': isActive(option.value) }"
      :aria-pressed="isActive(option.value)"
      :disabled="option.disabled"
      @click="select(option.value)"
    >
      <FontAwesomeIcon
        v-if="option.icon"
        :icon="option.icon"
        class="segmented-control__icon"
      />
      <span>{{ option.label }}</span>
      <span
        v-if="option.count"
        class="segmented-control__count"
        :class="{ 'segmented-control__count--muted': option.countMuted }"
      >
        {{ option.count }}
      </span>
    </button>
  </div>
</template>

<script setup lang="ts" generic="T extends string | number">
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";

export interface SegmentedControlOption<V extends string | number> {
  value: V;
  label: string;
  icon?: string | string[];
  disabled?: boolean;
  /** Small pill after the label, e.g. open item counts. Hidden when 0/undefined. */
  count?: number;
  /** Render the count pill in neutral grey instead of the attention colour. */
  countMuted?: boolean;
}

defineProps<{
  options: SegmentedControlOption<T>[];
  variant?: "default" | "primary";
  /** Size to the options instead of stretching to the container width. */
  fit?: boolean;
}>();

const model = defineModel<T>({ required: true });

const isActive = (value: T) => model.value === value;

const select = (value: T) => {
  if (value === model.value) return;
  model.value = value;
};
</script>

<style scoped lang="scss">
.segmented-control {
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: 1fr;
  background: var(--phoenix-gray-100);
  padding: 4px;
  border-radius: 10px;
  gap: 2px;
  width: 100%;

  button {
    all: unset;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 9px 12px;
    border-radius: 7px;
    font-size: 13px;
    font-weight: 600;
    color: var(--phoenix-gray-600);
    cursor: pointer;
    transition: all 0.12s ease;
    text-align: center;
    box-sizing: border-box;

    &:hover:not(:disabled) {
      color: var(--phoenix-gray-1000);
    }

    &:disabled {
      cursor: not-allowed;
      opacity: 0.55;
    }

    &.is-active {
      background: #ffffff;
      color: var(--phoenix-gray-1000);
      box-shadow:
        0 1px 2px rgba(var(--phoenix-gray-1000-rgb), 0.04),
        0 0 0 1px var(--phoenix-border-color-translucent);

      .segmented-control__icon {
        color: var(--phoenix-primary);
      }
    }
  }

  &.is-fit {
    width: max-content;
    grid-auto-columns: auto;
  }

  &.is-primary button.is-active {
    background: var(--phoenix-primary);
    color: #ffffff;
    box-shadow: 0 1px 2px rgba(var(--phoenix-primary-rgb), 0.25);

    .segmented-control__icon {
      color: #ffffff;
    }
  }
}

.segmented-control__icon {
  color: var(--phoenix-primary);
}

.segmented-control__count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 17px;
  height: 17px;
  padding: 0 5px;
  border-radius: 999px;
  background: var(--phoenix-orange-300);
  color: var(--phoenix-orange-900);
  font-size: 11px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;

  &--muted {
    background: var(--phoenix-gray-200);
    color: var(--phoenix-gray-800);
  }
}
</style>
